import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { DateFormat, FormDateField } from '../../../components/hook-form/form-date.field.component';
import dayjs from 'dayjs';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useSaveData } from '../../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { useEffect, useState } from 'react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { holidayManagementApiKeyStore } from '../../../api/masters/holiday-management-api-key-store';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';

const today = dayjs().startOf('day');

const validationSchema = Yup.object().shape({
  proposedScheduledDate: Yup.date()
    .required('Proposed schedule date is required')
    .typeError('Please enter a valid date') // Ensures the date is valid
    .min(today, 'Proposed schedule date must be in the future'),
});

interface ReadyDialogComponent {
  workorderID: any;
  onScheduleDate: (isPrerequest: boolean, date: any) => void;
  onClosed: () => void;
  inspectionRequestOn?: string;
  isParentLoading: boolean;
}

export const ReadyDialogComponent = ({ workorderID, onScheduleDate, onClosed, inspectionRequestOn, isParentLoading }: ReadyDialogComponent) => {

  const [openDialog, setOpenDialog] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<any>();
  const [pendingRuleResponse, setPendingErrorRoles] = useState<any>();
  const ishavePermission = useAuthorize([UserPermissionName?.OVERRIDE]);

  const { isLoading, mutateAsync } = useSaveData<any>(workOrdersApiKeyStore.checkPrerequisitesBeforeReady());

  const parsedInspectionRequestOn = dayjs(inspectionRequestOn, 'MM/DD/YYYY')?.isValid()
    ? dayjs(inspectionRequestOn)
    : null;

  const form = useReactHookForm({
    defaultValues: {
      proposedScheduledDate: parsedInspectionRequestOn,
    },
    yupSchema: validationSchema,
  });

  const { showSnackbar } = useSnackbar();

  const isHolidayOrWeekend = (scheduleDate: any, holidayDates: any) => {
    const selectedDate = dayjs(scheduleDate, "MM/DD/YYYY");

    // Check weekend
    const day = selectedDate.day(); // 0 = Sunday, 6 = Saturday
    const isWeekend = day === 0 || day === 6;

    // Check holiday
    const isHoliday = holidayDates?.some((holiday: any) =>
      dayjs(holiday.date, "MM/DD/YYYY").isSame(selectedDate, "day")
    );

    return {
      isWeekend,
      isHoliday,
      isInvalid: isWeekend || isHoliday,
    };
  };

  const handleSubmit = async (data: any) => {

    const result = isHolidayOrWeekend(dayjs(data?.proposedScheduledDate)?.format('MM/DD/YYYY'), holidayDates);

    if (result?.isHoliday && shouldRestrictDateSelection) {
      showSnackbar(
        'The selected proposed schedule date falls on a holiday. Please choose a valid working day to proceed.',
        'error'
      );
      return;
    }
    if (result?.isWeekend && shouldRestrictDateSelection) {
      showSnackbar(
        'The selected proposed schedule date falls on a weekend. Please choose a valid working day to proceed.',
        'error'
      );
      return;
    }

    const rulePayload = {
      workOrderID: workorderID,
      proposedScheduleDate: dayjs(data?.proposedScheduledDate)?.format('MM/DD/YYYY')
    }
    const ruleResponse: any = await mutateAsync({ data: rulePayload });

    const hasPendingRule = ruleResponse?.data?.pendingRules?.length;
    if (hasPendingRule > 0) {
      setPendingErrorRoles(ruleResponse?.data)
      setOpenDialog(true);
      setScheduleDate(data?.proposedScheduledDate);
    }
    else {
      onScheduleDate(false, data?.proposedScheduledDate);
    }
  };

  const closeValidationDialog = () => {
    setOpenDialog(false);
    setPendingErrorRoles(undefined);
  }

  const handleSumbitConfirm = () => {
    closeValidationDialog();
    onScheduleDate(true, scheduleDate);
  }

  const [holidayDates, setHolidayDates] = useState<any[]>([]);
  const { isLoading: isHolidayLoading, data: holidayData } = useLoadData<ApiResponse<any[]>>(
    holidayManagementApiKeyStore.getHolidays());

  useEffect(() => {
    if (holidayData) {
      const datesResult = holidayData?.data
        .filter((item: any) => item.isActive)
        //  ?.map((item: any) => item.holidayDate);
        ?.map((item: any) => ({
          date: item.holidayDate,
          name: item.holidayName,
        }));
      setHolidayDates(datesResult);
    }
  }, [holidayData])

  //const isWeekendHolidaySchedulingOverridePermission = !useAuthorize([UserPermissionName?.WEEKEND_HOLIDAY_SCHEDULING_OVERRIDE]);

  const hasWeekendHolidayOverridePermission = useAuthorize([
    UserPermissionName.WEEKEND_HOLIDAY_SCHEDULING_OVERRIDE,
  ]);

  const shouldRestrictDateSelection = !hasWeekendHolidayOverridePermission;

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <Dialog
        open={true}
        fullWidth={true}
        maxWidth={'sm'}
      // onClose={onClosed}
      >
        <DialogTitle id="scroll-dialog-title">Proposed Schedule Date</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClosed}
          sx={{
            position: 'absolute',
            right: 10,
            top: 15,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Tooltip title={<Typography variant="body1">Close</Typography>}>
            <CloseIcon />
          </Tooltip>
        </IconButton>
        <ReactHookForm formName="PROJECT_FORM" onSubmit={handleSubmit} formContextProps={form}>
          <DialogContent dividers className="popupDilog">
            <Grid container spacing={2}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormDateField 
                  label="Proposed Schedule Date"
                  name="proposedScheduledDate"
                  dateLimits={{ minDateEnabled: true }}
                  format={DateFormat.SIMPLE_DATE}
                  // restrictDateSelection={isWeekendHolidaySchedulingOverridePermission}
                  // holidayDateList={isWeekendHolidaySchedulingOverridePermission ? holidayDates : []}
                  restrictDateSelection={shouldRestrictDateSelection}
                  holidayDateList={holidayDates}
                  enableDateConfirmation={hasWeekendHolidayOverridePermission}
                  isParentLoading={isHolidayLoading}
                  disabled={isHolidayLoading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
            <DialogActions className="spaceBetween">
              <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" style={{ width: '100px' }} disabled={isLoading || isParentLoading || isReadOnlyPermission}>
                Save
              </Button>
            </DialogActions>
          </div>
        </ReactHookForm>
      </Dialog>
      {openDialog && (
        <Dialog
          open={openDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                <h4>Please see the below validation</h4>
                <br></br>
                <ul>
                  {pendingRuleResponse?.pendingRules?.map((rules: any) => (
                    <div key={`${pendingRuleResponse?.workorderID}-${rules}`}>
                      {/* <li style={{ marginLeft: '25px' }}> */}
                      <span>{rules}</span>
                      {/* </li> */}
                      {rules.includes("pre-requisite") && pendingRuleResponse?.preReqWorkOrders?.length > 0 && (
                        <dl>
                          <dt>Pending Work Orders:</dt>
                          {pendingRuleResponse?.preReqWorkOrders?.map((data: any) => <dd key={data}>- {data}</dd>)}
                        </dl>
                      )}
                    </div>
                  ))}
                </ul>
              </div>
              <br></br>
              <div>
                {ishavePermission ? (
                  <h5>
                    You have permission to change the status to ready without completing the validation.
                    Are you sure you want to continue?
                  </h5>
                ) : (
                  <h5>You can not change the status to ready without completing the validation.</h5>
                )}
              </div>
              <LoadingIndicator isLoading={isLoading} />
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeValidationDialog}
                autoFocus
              >
                Cancel
              </button>
              {ishavePermission && (
                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  type="button"
                  onClick={handleSumbitConfirm}
                  disabled={isLoading}
                  style={{ cursor: 'pointer' }}
                >
                  Confirm
                </button>
              )}
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};
